import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Pencil, ArrowRightLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  expensesApi,
  type ExpenseEmployeeRow,
  type SalaryRunPreview,
} from "@/api/expenses";

const CENTRAL_HOSPITAL_SELECT_CENTRAL = "CENTRAL";

export type SalaryRosterPanelProps = {
  canManage: boolean;
  /** Chain / sub-admin only — book monthly salaries as paid expenses. */
  canBookSalaries?: boolean;
  /** Ops manager: roster limited to their hospital (hide Central / sibling sites). */
  hospitalScopedOnly?: boolean;
  readOnly?: boolean;
  isChainAdmin: boolean;
  chainHospitalOptions: Array<{ value: string; label: string }>; // includes CENTRAL as value "CENTRAL"
  paymentModes: string[];
  hospitalIdParam?: string; // for super admin
};

function monthLocalISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function todayLocalISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function toDateInputValue(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatEmploymentDate(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function formatMoney(n: number): string {
  return `₹${Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function paymentModeLabel(mode: string): string {
  return mode.replace(/_/g, " ");
}

function unwrapAxios<T>(res: { data?: { data?: T; message?: string } }): T | undefined {
  return res.data?.data;
}

function errMessage(e: unknown, fallback: string): string {
  if (e instanceof Error && e.message) return e.message;
  const ax = e as { response?: { data?: { message?: string } }; message?: string };
  return ax?.response?.data?.message || ax?.message || fallback;
}

function unitLabel(
  row: ExpenseEmployeeRow,
  options: Array<{ value: string; label: string }>
): string {
  if (row.scope === "CENTRAL") return "Central";
  if (row.hospital?.name) return row.hospital.name;
  const match = options.find((o) => o.value === row.hospitalId);
  return match?.label ?? "—";
}

function defaultHospitalSelect(
  isChainAdmin: boolean,
  options: Array<{ value: string; label: string }>,
  activeHospitalId: string,
  hospitalScopedOnly = false
): string {
  if (isChainAdmin && !hospitalScopedOnly) {
    const rudram = options.find((o) => /rudram/i.test(o.label));
    return rudram?.value ?? options.find((o) => o.value !== CENTRAL_HOSPITAL_SELECT_CENTRAL)?.value ?? CENTRAL_HOSPITAL_SELECT_CENTRAL;
  }
  return activeHospitalId || options[0]?.value || "";
}

type EditForm = {
  id?: string;
  name: string;
  monthlyAmount: string;
  hospitalSelect: string;
  bankDetails: string;
  notes: string;
  isActive: boolean;
  lastEmploymentDate: string;
};

export default function SalaryRosterPanel({
  canManage,
  canBookSalaries = true,
  hospitalScopedOnly = false,
  readOnly = false,
  isChainAdmin,
  chainHospitalOptions,
  paymentModes,
  hospitalIdParam,
}: SalaryRosterPanelProps) {
  const { activeHospitalId, user } = useAuth();
  const qc = useQueryClient();
  const lockedHospitalId = hospitalScopedOnly
    ? user?.hospitalId || activeHospitalId || ""
    : activeHospitalId || user?.hospitalId || "";
  const manageEnabled = canManage && !readOnly;
  const bookEnabled = canBookSalaries && !readOnly;

  const hospitalOpts = useMemo(() => {
    if (isChainAdmin && !hospitalScopedOnly) return chainHospitalOptions;
    const label =
      user?.hospitals?.find((h) => h.id === lockedHospitalId)?.name ||
      user?.hospitals?.[0]?.name ||
      "This hospital";
    return lockedHospitalId
      ? [{ value: lockedHospitalId, label }]
      : chainHospitalOptions.filter((o) => o.value !== CENTRAL_HOSPITAL_SELECT_CENTRAL);
  }, [isChainAdmin, hospitalScopedOnly, chainHospitalOptions, lockedHospitalId, user?.hospitals]);

  const [serviceMonth, setServiceMonth] = useState(monthLocalISO);
  const [runPaymentMode, setRunPaymentMode] = useState("");
  const [employeePaymentModes, setEmployeePaymentModes] = useState<Record<string, string>>(
    {}
  );
  const [employeeAmounts, setEmployeeAmounts] = useState<Record<string, string>>({});
  const [includeInactive, setIncludeInactive] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState<EditForm>({
    name: "",
    monthlyAmount: "",
    hospitalSelect: defaultHospitalSelect(isChainAdmin, hospitalOpts, lockedHospitalId, hospitalScopedOnly),
    bankDetails: "",
    notes: "",
    isActive: true,
    lastEmploymentDate: "",
  });

  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deactivateRow, setDeactivateRow] = useState<ExpenseEmployeeRow | null>(null);
  const [deactivateLastDay, setDeactivateLastDay] = useState(todayLocalISO);

  const [moveOpen, setMoveOpen] = useState(false);
  const [moveEmployee, setMoveEmployee] = useState<ExpenseEmployeeRow | null>(null);
  const [moveTarget, setMoveTarget] = useState(CENTRAL_HOSPITAL_SELECT_CENTRAL);

  const hospitalIdQuery =
    hospitalScopedOnly || !hospitalIdParam ? {} : { hospitalId: hospitalIdParam };

  const { data: preview, isFetching: previewLoading } = useQuery({
    queryKey: ["salary-run-preview", serviceMonth, hospitalIdParam],
    queryFn: async () => {
      const r = await expensesApi.previewSalaryRun({
        serviceMonth,
        ...hospitalIdQuery,
      });
      return (unwrapAxios(r) as SalaryRunPreview | undefined) ?? null;
    },
    enabled: bookEnabled && !!serviceMonth && (!hospitalIdParam || !!hospitalIdParam),
    staleTime: 15_000,
  });

  const toBookLines = useMemo(
    () => (preview?.lines ?? []).filter((l) => !l.alreadyBooked),
    [preview]
  );

  // Keep per-employee modes in sync with preview; fill blanks from default at seed time.
  useEffect(() => {
    if (!preview) return;
    setEmployeePaymentModes((prev) => {
      const next: Record<string, string> = {};
      for (const line of preview.lines) {
        if (line.alreadyBooked) continue;
        next[line.employeeId] = prev[line.employeeId] || runPaymentMode || "";
      }
      return next;
    });
  }, [preview, runPaymentMode]);

  // Seed editable run amounts from roster preview (keep edits for same employee ids).
  useEffect(() => {
    if (!preview) return;
    setEmployeeAmounts((prev) => {
      const next: Record<string, string> = {};
      for (const line of preview.lines) {
        if (line.alreadyBooked) continue;
        const existing = prev[line.employeeId];
        next[line.employeeId] =
          existing != null && existing !== "" ? existing : String(line.amount);
      }
      return next;
    });
  }, [preview]);

  const lineAmount = (employeeId: string, fallback: number): number => {
    const raw = employeeAmounts[employeeId];
    if (raw == null || String(raw).trim() === "") return fallback;
    const n = Number(raw);
    return Number.isFinite(n) ? n : NaN;
  };

  const applyDefaultModeToAll = () => {
    if (!runPaymentMode) {
      toast.error("Select a default payment mode first");
      return;
    }
    setEmployeePaymentModes((prev) => {
      const next = { ...prev };
      for (const line of toBookLines) {
        next[line.employeeId] = runPaymentMode;
      }
      return next;
    });
  };

  const { data: roster = [], isLoading: rosterLoading } = useQuery({
    queryKey: ["salary-roster", includeInactive, hospitalIdParam],
    queryFn: async () => {
      const r = await expensesApi.listSalaryRoster({
        includeInactive,
        ...hospitalIdQuery,
      });
      return (
        (unwrapAxios(r) as { employees?: ExpenseEmployeeRow[] } | undefined)?.employees ?? []
      );
    },
    staleTime: 15_000,
  });

  const visibleRoster = useMemo(() => {
    if (!hospitalScopedOnly) return roster;
    return roster.filter(
      (row) => row.scope !== "CENTRAL" && row.hospitalId === lockedHospitalId
    );
  }, [roster, hospitalScopedOnly, lockedHospitalId]);

  const invalidateRoster = () => {
    void qc.invalidateQueries({ queryKey: ["salary-roster"] });
    void qc.invalidateQueries({ queryKey: ["salary-run-preview"] });
    void qc.invalidateQueries({ queryKey: ["expense-central-employees"] });
  };

  const runMut = useMutation({
    mutationFn: async () => {
      if (!serviceMonth) throw new Error("Select a service month");
      const modes: Record<string, string> = {};
      const amounts: Record<string, number> = {};
      for (const line of toBookLines) {
        const mode = employeePaymentModes[line.employeeId] || runPaymentMode;
        if (!mode) {
          throw new Error(`Select a payment mode for ${line.name}`);
        }
        const amount = lineAmount(line.employeeId, line.amount);
        if (!Number.isFinite(amount) || amount <= 0) {
          throw new Error(`Enter a valid amount for ${line.name}`);
        }
        modes[line.employeeId] = mode;
        amounts[line.employeeId] = amount;
      }
      const r = await expensesApi.runSalaryRun({
        serviceMonth,
        ...(runPaymentMode ? { paymentMode: runPaymentMode } : {}),
        employeePaymentModes: modes,
        employeeAmounts: amounts,
        ...hospitalIdQuery,
      });
      return unwrapAxios(r) as { createdCount?: number; createdTotal?: number } | undefined;
    },
    onSuccess: (result) => {
      const n = result?.createdCount ?? 0;
      toast.success(
        n > 0
          ? `Booked ${n} salary expense(s) as paid (${formatMoney(result?.createdTotal ?? 0)})`
          : "No new salaries to book for this month"
      );
      invalidateRoster();
    },
    onError: (e) => toast.error(errMessage(e, "Failed to book salaries")),
  });

  const upsertMut = useMutation({
    mutationFn: async (body: Parameters<typeof expensesApi.upsertSalaryRoster>[0]) => {
      const r = await expensesApi.upsertSalaryRoster(body);
      return unwrapAxios(r);
    },
    onSuccess: () => {
      toast.success("Salary employee saved");
      setEditOpen(false);
      invalidateRoster();
    },
    onError: (e) => toast.error(errMessage(e, "Failed to save employee")),
  });

  const moveMut = useMutation({
    mutationFn: async () => {
      if (!moveEmployee) throw new Error("No employee selected");
      const body =
        moveTarget === CENTRAL_HOSPITAL_SELECT_CENTRAL
          ? { hospitalUnit: "CENTRAL" as const }
          : { targetHospitalId: moveTarget };
      const r = await expensesApi.moveSalaryRoster(moveEmployee.id, body);
      return unwrapAxios(r);
    },
    onSuccess: () => {
      toast.success("Employee moved");
      setMoveOpen(false);
      setMoveEmployee(null);
      invalidateRoster();
    },
    onError: (e) => toast.error(errMessage(e, "Failed to move employee")),
  });

  const toBookCount = toBookLines.length;
  const skippedCount = preview?.skippedCount ?? 0;
  const toBookTotal = useMemo(() => {
    return toBookLines.reduce((sum, line) => {
      const raw = employeeAmounts[line.employeeId];
      const amount =
        raw == null || String(raw).trim() === "" ? line.amount : Number(raw);
      return sum + (Number.isFinite(amount) && amount > 0 ? amount : 0);
    }, 0);
  }, [toBookLines, employeeAmounts]);
  const unitSummaries = useMemo(() => {
    const map = new Map<string, { unit: string; count: number; total: number }>();
    for (const line of toBookLines) {
      const unit = line.hospitalName || "Unknown";
      const raw = employeeAmounts[line.employeeId];
      const amount =
        raw == null || String(raw).trim() === "" ? line.amount : Number(raw);
      const add = Number.isFinite(amount) && amount > 0 ? amount : 0;
      const prev = map.get(unit) ?? { unit, count: 0, total: 0 };
      map.set(unit, { unit, count: prev.count + 1, total: prev.total + add });
    }
    return [...map.values()].sort((a, b) => a.unit.localeCompare(b.unit));
  }, [toBookLines, employeeAmounts]);
  const allToBookHaveModes =
    toBookLines.length > 0 &&
    toBookLines.every((l) => !!(employeePaymentModes[l.employeeId] || runPaymentMode));
  const allToBookHaveAmounts =
    toBookLines.length > 0 &&
    toBookLines.every((l) => {
      const amount = lineAmount(l.employeeId, l.amount);
      return Number.isFinite(amount) && amount > 0;
    });
  const canRun =
    bookEnabled &&
    toBookCount > 0 &&
    allToBookHaveModes &&
    allToBookHaveAmounts &&
    !!serviceMonth &&
    !runMut.isPending;

  const openAdd = () => {
    setEditForm({
      name: "",
      monthlyAmount: "",
      hospitalSelect: defaultHospitalSelect(isChainAdmin, hospitalOpts, lockedHospitalId, hospitalScopedOnly),
      bankDetails: "",
      notes: "",
      isActive: true,
      lastEmploymentDate: "",
    });
    setEditOpen(true);
  };

  const openEdit = (row: ExpenseEmployeeRow) => {
    setEditForm({
      id: row.id,
      name: row.name,
      monthlyAmount:
        row.monthlyAmount != null && Number.isFinite(Number(row.monthlyAmount))
          ? String(row.monthlyAmount)
          : "",
      hospitalSelect:
        row.scope === "CENTRAL"
          ? CENTRAL_HOSPITAL_SELECT_CENTRAL
          : row.hospitalId || lockedHospitalId,
      bankDetails: row.bankDetails || "",
      notes: row.notes || "",
      isActive: row.isActive !== false,
      lastEmploymentDate: toDateInputValue(row.lastEmploymentDate),
    });
    setEditOpen(true);
  };

  const openMove = (row: ExpenseEmployeeRow) => {
    setMoveEmployee(row);
    setMoveTarget(
      row.scope === "CENTRAL"
        ? CENTRAL_HOSPITAL_SELECT_CENTRAL
        : row.hospitalId ||
            hospitalOpts.find((o) => o.value !== CENTRAL_HOSPITAL_SELECT_CENTRAL)?.value ||
            CENTRAL_HOSPITAL_SELECT_CENTRAL
    );
    setMoveOpen(true);
  };

  const submitEdit = () => {
    const name = editForm.name.trim();
    const monthlyAmount = Number(editForm.monthlyAmount);
    if (!name) {
      toast.error("Name is required");
      return;
    }
    if (!Number.isFinite(monthlyAmount) || monthlyAmount <= 0) {
      toast.error("Enter a valid monthly amount");
      return;
    }
    if (!editForm.hospitalSelect) {
      toast.error("Select hospital / unit");
      return;
    }
    if (!editForm.isActive && !editForm.lastEmploymentDate.trim()) {
      toast.error("Last day of employment is required when deactivating");
      return;
    }

    const body: Parameters<typeof expensesApi.upsertSalaryRoster>[0] = {
      ...(editForm.id ? { id: editForm.id } : {}),
      name,
      monthlyAmount,
      bankDetails: editForm.bankDetails.trim() || undefined,
      notes: editForm.notes.trim() || undefined,
      isActive: editForm.isActive,
      ...(!editForm.isActive
        ? { lastEmploymentDate: editForm.lastEmploymentDate.trim() }
        : {}),
    };

    if (editForm.hospitalSelect === CENTRAL_HOSPITAL_SELECT_CENTRAL) {
      body.hospitalUnit = "CENTRAL";
      body.scope = "CENTRAL";
    } else {
      body.targetHospitalId = editForm.hospitalSelect;
      body.scope = "UNIT";
    }

    upsertMut.mutate(body);
  };

  const openDeactivate = (row: ExpenseEmployeeRow) => {
    if (!manageEnabled) return;
    const amount = Number(row.monthlyAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Employee has no valid monthly amount; edit first");
      return;
    }
    setDeactivateRow(row);
    setDeactivateLastDay(todayLocalISO());
    setDeactivateOpen(true);
  };

  const submitDeactivate = () => {
    if (!deactivateRow) return;
    if (!deactivateLastDay.trim()) {
      toast.error("Select last day of employment");
      return;
    }
    const amount = Number(deactivateRow.monthlyAmount);
    const body: Parameters<typeof expensesApi.upsertSalaryRoster>[0] = {
      id: deactivateRow.id,
      name: deactivateRow.name,
      monthlyAmount: amount,
      bankDetails: deactivateRow.bankDetails || undefined,
      notes: deactivateRow.notes || undefined,
      isActive: false,
      lastEmploymentDate: deactivateLastDay.trim(),
    };
    if (deactivateRow.scope === "CENTRAL") {
      body.hospitalUnit = "CENTRAL";
      body.scope = "CENTRAL";
    } else if (deactivateRow.hospitalId) {
      body.targetHospitalId = deactivateRow.hospitalId;
      body.scope = "UNIT";
    }
    upsertMut.mutate(body, {
      onSuccess: () => {
        setDeactivateOpen(false);
        setDeactivateRow(null);
      },
    });
  };

  const onBookClick = () => {
    if (!canRun) return;
    const distinct = new Set(
      toBookLines.map((l) => employeePaymentModes[l.employeeId] || runPaymentMode)
    );
    const modeSummary =
      distinct.size === 1
        ? paymentModeLabel([...distinct][0] || "")
        : `${distinct.size} payment modes`;
    const ok = window.confirm(
      `Book ${toBookCount} salary expense(s) totaling ${formatMoney(toBookTotal)} for ${serviceMonth} as paid (${modeSummary})?`
    );
    if (!ok) return;
    runMut.mutate();
  };

  return (
    <div className="space-y-6">
      {bookEnabled ? (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Book monthly salaries</CardTitle>
          <p className="text-muted-foreground text-sm font-normal">
            One-tap: create PAID central salary expenses for every active roster employee not yet
            booked for the selected service month. Edit amount and payment mode per employee before
            booking.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2">
              <Label htmlFor="salary-service-month">Service month</Label>
              <Input
                id="salary-service-month"
                type="month"
                value={serviceMonth}
                onChange={(e) => setServiceMonth(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Default payment mode</Label>
              <Select
                value={runPaymentMode || "__none__"}
                onValueChange={(v) => setRunPaymentMode(v === "__none__" ? "" : v)}
                disabled={!manageEnabled}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Optional default" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No default</SelectItem>
                  {paymentModes.map((m) => (
                    <SelectItem key={m} value={m}>
                      {paymentModeLabel(m)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col justify-end gap-2 sm:col-span-2">
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={applyDefaultModeToAll}
                  disabled={!manageEnabled || !runPaymentMode || toBookCount === 0}
                >
                  Apply default to all
                </Button>
                {previewLoading ? (
                  <span className="text-muted-foreground flex items-center gap-2 text-sm">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading preview…
                  </span>
                ) : preview ? (
                  <span className="text-sm">
                    To book: <strong>{toBookCount}</strong> · Total:{" "}
                    <strong>{formatMoney(toBookTotal)}</strong>
                    <span className="text-muted-foreground">
                      {" "}
                      · Skipped: {skippedCount}
                    </span>
                  </span>
                ) : (
                  <span className="text-muted-foreground text-sm">
                    Select a service month to preview.
                  </span>
                )}
              </div>
              {unitSummaries.length > 0 && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                  {unitSummaries.map((u) => (
                    <span key={u.unit}>
                      <strong>{u.unit}</strong>: {u.count} employee
                      {u.count === 1 ? "" : "s"}, {formatMoney(u.total)}
                    </span>
                  ))}
                </div>
              )}
              <p className="text-muted-foreground text-xs">
                Amounts and payment modes can differ per employee. Edited amounts are booked to
                expenses / P&amp;L / cash flow for this run only (roster default stays until you edit
                the employee).
              </p>
            </div>
          </div>

          {toBookLines.length > 0 && (
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Hospital / unit</TableHead>
                    <TableHead className="min-w-[8rem] text-right">Amount (₹)</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="min-w-[10rem]">Payment mode</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {toBookLines.map((line) => (
                    <TableRow key={line.employeeId}>
                      <TableCell className="font-medium">{line.name}</TableCell>
                      <TableCell>{line.hospitalName}</TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          min={0}
                          step="0.01"
                          className="ml-auto h-8 w-32 text-right tabular-nums"
                          value={employeeAmounts[line.employeeId] ?? String(line.amount)}
                          onChange={(e) =>
                            setEmployeeAmounts((prev) => ({
                              ...prev,
                              [line.employeeId]: e.target.value,
                            }))
                          }
                          disabled={!manageEnabled}
                        />
                      </TableCell>
                      <TableCell className="text-muted-foreground max-w-[14rem] text-xs">
                        {line.prorationNote || "—"}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={
                            employeePaymentModes[line.employeeId] ||
                            runPaymentMode ||
                            "__none__"
                          }
                          onValueChange={(v) =>
                            setEmployeePaymentModes((prev) => ({
                              ...prev,
                              [line.employeeId]: v === "__none__" ? "" : v,
                            }))
                          }
                          disabled={!manageEnabled}
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue placeholder="Required" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__" disabled>
                              Select mode
                            </SelectItem>
                            {paymentModes.map((m) => (
                              <SelectItem key={m} value={m}>
                                {paymentModeLabel(m)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <Button onClick={onBookClick} disabled={!canRun}>
            {runMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Book monthly salaries as paid
          </Button>
        </CardContent>
      </Card>
      ) : null}

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0 pb-3">
          <div>
            <CardTitle className="text-base">Salary roster</CardTitle>
            <p className="text-muted-foreground mt-1 text-sm font-normal">
              Employees with a fixed monthly amount. Active rows are included in salary runs.
              {!bookEnabled && manageEnabled
                ? " Showing site employees at your hospital only (Central and other chain locations are hidden). Booking salaries is admin-only."
                : ""}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Checkbox
                id="salary-include-inactive"
                checked={includeInactive}
                onCheckedChange={(v) => setIncludeInactive(v === true)}
              />
              <Label htmlFor="salary-include-inactive" className="text-sm font-normal">
                Show inactive
              </Label>
            </div>
            {manageEnabled && (
              <Button type="button" size="sm" onClick={openAdd}>
                Add employee
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {rosterLoading ? (
            <p className="text-muted-foreground flex items-center gap-2 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading roster…
            </p>
          ) : visibleRoster.length === 0 ? (
            <p className="text-muted-foreground text-sm">No employees on the salary roster yet.</p>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="text-right">Monthly ₹</TableHead>
                    <TableHead>Hospital / unit</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead>Last employment</TableHead>
                    {manageEnabled && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleRoster.map((row) => (
                    <TableRow key={row.id} className={row.isActive === false ? "opacity-60" : undefined}>
                      <TableCell className="font-medium">{row.name}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMoney(Number(row.monthlyAmount ?? 0))}
                      </TableCell>
                      <TableCell>{unitLabel(row, hospitalOpts)}</TableCell>
                      <TableCell>
                        {row.isActive === false ? (
                          <Badge variant="secondary">Inactive</Badge>
                        ) : (
                          <Badge variant="default">Active</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {row.isActive === false
                          ? formatEmploymentDate(row.lastEmploymentDate)
                          : "—"}
                      </TableCell>
                      {manageEnabled && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => openEdit(row)}
                              title="Edit"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            {isChainAdmin && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => openMove(row)}
                                title="Move"
                              >
                                <ArrowRightLeft className="h-4 w-4" />
                              </Button>
                            )}
                            {row.isActive !== false && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-destructive"
                                onClick={() => openDeactivate(row)}
                                disabled={upsertMut.isPending}
                              >
                                Deactivate
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editForm.id ? "Edit salary employee" : "Add salary employee"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="sr-name">Name</Label>
              <Input
                id="sr-name"
                value={editForm.name}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sr-amount">Monthly amount (₹)</Label>
              <Input
                id="sr-amount"
                type="number"
                min={0}
                step="0.01"
                value={editForm.monthlyAmount}
                onChange={(e) => setEditForm((f) => ({ ...f, monthlyAmount: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Hospital / unit</Label>
              {isChainAdmin && !hospitalScopedOnly ? (
                <Select
                  value={editForm.hospitalSelect}
                  onValueChange={(v) => setEditForm((f) => ({ ...f, hospitalSelect: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {hospitalOpts.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={hospitalOpts.find((o) => o.value === editForm.hospitalSelect)?.label || "This hospital"}
                  disabled
                />
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="sr-bank">Bank details</Label>
              <Textarea
                id="sr-bank"
                rows={2}
                value={editForm.bankDetails}
                onChange={(e) => setEditForm((f) => ({ ...f, bankDetails: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sr-notes">Notes</Label>
              <Textarea
                id="sr-notes"
                rows={2}
                value={editForm.notes}
                onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>
            {bookEnabled ? (
              <>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="sr-active"
                    checked={editForm.isActive}
                    onCheckedChange={(v) =>
                      setEditForm((f) => ({
                        ...f,
                        isActive: v === true,
                        lastEmploymentDate: v === true ? "" : f.lastEmploymentDate || todayLocalISO(),
                      }))
                    }
                  />
                  <Label htmlFor="sr-active" className="text-sm font-normal">
                    Active (include in salary runs)
                  </Label>
                </div>
                {!editForm.isActive && (
                  <div className="space-y-2">
                    <Label htmlFor="sr-last-day">Last day of employment</Label>
                    <Input
                      id="sr-last-day"
                      type="date"
                      value={editForm.lastEmploymentDate}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, lastEmploymentDate: e.target.value }))
                      }
                    />
                    <p className="text-muted-foreground text-xs">
                      Final-month salary is paid only through this date (prorated in the salary run).
                    </p>
                  </div>
                )}
              </>
            ) : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={submitEdit} disabled={upsertMut.isPending}>
              {upsertMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deactivateOpen}
        onOpenChange={(open) => {
          setDeactivateOpen(open);
          if (!open) setDeactivateRow(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Deactivate {deactivateRow?.name ?? "employee"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-muted-foreground text-sm">
              They will be removed from future salary runs. For the month they leave, salary is
              paid only through their last day of employment.
            </p>
            <div className="space-y-2">
              <Label htmlFor="deactivate-last-day">Last day of employment</Label>
              <Input
                id="deactivate-last-day"
                type="date"
                value={deactivateLastDay}
                onChange={(e) => setDeactivateLastDay(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeactivateOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={submitDeactivate}
              disabled={upsertMut.isPending}
            >
              {upsertMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Deactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={moveOpen}
        onOpenChange={(open) => {
          setMoveOpen(open);
          if (!open) setMoveEmployee(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Move {moveEmployee?.name ?? "employee"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Target hospital / unit</Label>
            <Select value={moveTarget} onValueChange={setMoveTarget}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {hospitalOpts.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setMoveOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={() => moveMut.mutate()} disabled={moveMut.isPending}>
              {moveMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Move
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
